import {
	Button,
	Calendar,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	cn,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Skeleton,
} from "@monbits/ui";
import { endOfDay, format, startOfDay, subDays } from "date-fns";
import { ArrowDownRight, ArrowUpRight, CalendarIcon } from "lucide-react";
import * as React from "react";
import type { TooltipProps } from "recharts";
import {
	Area,
	AreaChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import analyticsProvider, {
	type OrderStatusAnalyticsResponse,
	type ProfitAnalyticsResponse,
	type SystemOverview,
} from "src/providers/analytics-provider";

type ChartTooltipProps = TooltipProps<number, string>;

// Custom tooltip component that respects theme
const CustomTooltip = ({
	active,
	payload,
	label,
	formatter,
}: ChartTooltipProps) => {
	if (!active || !payload || payload.length === 0) return null;

	return (
		<div className="rounded-lg border bg-popover p-3 text-popover-foreground shadow-md">
			<p className="mb-2 text-xs font-medium">{label}</p>
			{payload.map((entry, index) => {
				// Handle formatter that returns [value, name] or just value
				const entryValue = entry.value ?? 0;
				const entryName = entry.name ?? "";
				let displayValue: React.ReactNode = entryValue;
				let displayName: React.ReactNode = entryName;

				if (formatter) {
					const result = formatter(
						entryValue,
						entryName,
						entry,
						index,
						payload,
					);
					if (Array.isArray(result)) {
						displayValue = result[0];
						displayName = result[1];
					} else {
						displayValue = result;
					}
				}

				return (
					<div key={index} className="flex items-center gap-2 text-xs">
						<div
							className="h-2 w-2 rounded-full"
							style={{ backgroundColor: entry.color ?? "currentColor" }}
						/>
						<span className="text-muted-foreground">{displayName}:</span>
						<span className="font-medium">{displayValue}</span>
					</div>
				);
			})}
		</div>
	);
};

const DashboardPage: React.FC = () => {
	const [overview, setOverview] = React.useState<SystemOverview | null>(null);
	const [profitData, setProfitData] =
		React.useState<ProfitAnalyticsResponse | null>(null);
	const [orderStatusData, setOrderStatusData] =
		React.useState<OrderStatusAnalyticsResponse | null>(null);
	const [isLoading, setIsLoading] = React.useState(true);
	const [isProfitLoading, setIsProfitLoading] = React.useState(true);
	const [isOrderStatusLoading, setIsOrderStatusLoading] = React.useState(true);
	const [isProfitCalendarOpen, setIsProfitCalendarOpen] = React.useState(false);
	const [isOrderStatusCalendarOpen, setIsOrderStatusCalendarOpen] =
		React.useState(false);

	// Date range states - default to last 7 days
	const [profitDateRange, setProfitDateRange] = React.useState<{
		from: Date;
		to: Date;
	}>({
		from: startOfDay(subDays(new Date(), 6)), // 7 days ago (including today)
		to: endOfDay(new Date()),
	});

	const [orderStatusDateRange, setOrderStatusDateRange] = React.useState<{
		from: Date;
		to: Date;
	}>({
		from: startOfDay(subDays(new Date(), 6)), // 7 days ago (including today)
		to: endOfDay(new Date()),
	});

	// Calendar selection states (controlled)
	const [profitCalendarSelection, setProfitCalendarSelection] = React.useState<
		| {
				from: Date | undefined;
				to?: Date | undefined;
		  }
		| undefined
	>(undefined);

	const [orderStatusCalendarSelection, setOrderStatusCalendarSelection] =
		React.useState<
			| {
					from: Date | undefined;
					to?: Date | undefined;
			  }
			| undefined
		>(undefined);

	// Load overview once on mount
	React.useEffect(() => {
		const loadOverview = async () => {
			try {
				setIsLoading(true);
				const overviewData = await analyticsProvider.getSystemOverview();
				setOverview(overviewData);
			} catch (error) {
				console.error("Failed to load overview data:", error);
				toast.error("Failed to load dashboard overview", {
					description: "Please try again later",
				});
			} finally {
				setIsLoading(false);
			}
		};
		loadOverview();
	}, []);

	// Load profit analytics when date range changes
	React.useEffect(() => {
		const loadProfitAnalytics = async () => {
			try {
				setIsProfitLoading(true);
				const profitAnalytics = await analyticsProvider.getProfitAnalytics(
					format(profitDateRange.from, "yyyy-MM-dd"),
					format(profitDateRange.to, "yyyy-MM-dd"),
				);
				setProfitData(profitAnalytics);
			} catch (error) {
				console.error("Failed to load profit analytics:", error);
				toast.error("Failed to load profit analytics", {
					description: "Please try again later",
				});
			} finally {
				setIsProfitLoading(false);
			}
		};
		loadProfitAnalytics();
	}, [profitDateRange]);

	// Load order status analytics when date range changes
	React.useEffect(() => {
		const loadOrderStatusAnalytics = async () => {
			try {
				setIsOrderStatusLoading(true);
				const orderStatusAnalytics =
					await analyticsProvider.getOrderStatusAnalytics(
						format(orderStatusDateRange.from, "yyyy-MM-dd"),
						format(orderStatusDateRange.to, "yyyy-MM-dd"),
					);
				setOrderStatusData(orderStatusAnalytics);
			} catch (error) {
				console.error("Failed to load order status analytics:", error);
				toast.error("Failed to load order status analytics", {
					description: "Please try again later",
				});
			} finally {
				setIsOrderStatusLoading(false);
			}
		};
		loadOrderStatusAnalytics();
	}, [orderStatusDateRange]);

	// Helper functions
	const formatCurrency = (amount: number, currency: string) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency === "ALL" ? "USD" : currency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);
	};

	// Profit calendar handlers
	const handleProfitDateRangeChange = (
		range: { from: Date | undefined; to?: Date | undefined } | undefined,
	) => {
		setProfitCalendarSelection(range);
	};

	const handleApplyProfitDateRange = () => {
		if (profitCalendarSelection?.from && profitCalendarSelection?.to) {
			setProfitDateRange({
				from: startOfDay(profitCalendarSelection.from),
				to: endOfDay(profitCalendarSelection.to),
			});
			setIsProfitCalendarOpen(false);
			setProfitCalendarSelection(undefined);
		}
	};

	const handleCancelProfitDateRange = () => {
		setIsProfitCalendarOpen(false);
		setProfitCalendarSelection(undefined);
	};

	const handleProfitCalendarOpenChange = (open: boolean) => {
		setIsProfitCalendarOpen(open);
		if (!open) {
			setProfitCalendarSelection(undefined);
		}
	};

	// Order status calendar handlers
	const handleOrderStatusDateRangeChange = (
		range: { from: Date | undefined; to?: Date | undefined } | undefined,
	) => {
		setOrderStatusCalendarSelection(range);
	};

	const handleApplyOrderStatusDateRange = () => {
		if (
			orderStatusCalendarSelection?.from &&
			orderStatusCalendarSelection?.to
		) {
			setOrderStatusDateRange({
				from: startOfDay(orderStatusCalendarSelection.from),
				to: endOfDay(orderStatusCalendarSelection.to),
			});
			setIsOrderStatusCalendarOpen(false);
			setOrderStatusCalendarSelection(undefined);
		}
	};

	const handleCancelOrderStatusDateRange = () => {
		setIsOrderStatusCalendarOpen(false);
		setOrderStatusCalendarSelection(undefined);
	};

	const handleOrderStatusCalendarOpenChange = (open: boolean) => {
		setIsOrderStatusCalendarOpen(open);
		if (!open) {
			setOrderStatusCalendarSelection(undefined);
		}
	};

	// Check if we have valid ranges selected
	const hasProfitValidRange =
		profitCalendarSelection?.from &&
		profitCalendarSelection?.to &&
		profitCalendarSelection.from.toDateString() !==
			profitCalendarSelection.to.toDateString();

	const hasOrderStatusValidRange =
		orderStatusCalendarSelection?.from &&
		orderStatusCalendarSelection?.to &&
		orderStatusCalendarSelection.from.toDateString() !==
			orderStatusCalendarSelection.to.toDateString();

	if (isLoading) {
		return (
			<div className="space-y-6 p-4 sm:p-6">
				<Skeleton className="h-12 w-64" />
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
					{[...Array(4)].map((_, i) => (
						<Skeleton key={i} className="h-32" />
					))}
				</div>
				<div className="grid gap-6 md:grid-cols-2">
					{[...Array(2)].map((_, i) => (
						<Skeleton key={i} className="h-64" />
					))}
				</div>
				<Skeleton className="h-12 w-48" />
				<div className="grid gap-6 md:grid-cols-3">
					{[...Array(3)].map((_, i) => (
						<Skeleton key={i} className="h-48" />
					))}
				</div>
				<Skeleton className="h-96" />
			</div>
		);
	}

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<div>
				<h1 className="text-3xl font-bold">Dashboard</h1>
				<p className="text-sm text-muted-foreground mt-1">
					System overview and analytics
				</p>
			</div>

			<div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
				<div className="stagger-in rounded-xl border p-5 bg-green-50/80 text-green-900 border-green-200/50 dark:bg-green-950/25 dark:text-green-300 dark:border-green-500/20">
					<p className="text-sm font-medium opacity-70">Total Orders</p>
					<p className="text-3xl font-bold tracking-tight tabular-nums mt-2">
						{overview?.orders.total || 0}
					</p>
					<p className="text-xs opacity-50 mt-1">
						{overview?.orders.paid || 0} paid
					</p>
				</div>
				<div className="stagger-in rounded-xl border p-5 bg-blue-50/80 text-blue-900 border-blue-200/50 dark:bg-blue-950/25 dark:text-blue-300 dark:border-blue-500/20">
					<p className="text-sm font-medium opacity-70">Active Users</p>
					<p className="text-3xl font-bold tracking-tight tabular-nums mt-2">
						{overview?.platform.activeUsers || 0}
					</p>
					<p className="text-xs opacity-50 mt-1">
						{overview?.platform.activeMerchants || 0} merchants
					</p>
				</div>
				<div className="stagger-in rounded-xl border p-5 bg-amber-50/80 text-amber-900 border-amber-200/50 dark:bg-amber-950/25 dark:text-amber-300 dark:border-amber-500/20">
					<p className="text-sm font-medium opacity-70">Recent Activity</p>
					<p className="text-3xl font-bold tracking-tight tabular-nums mt-2">
						{overview?.recentActivity.last30Days || 0}
					</p>
					<p className="text-xs opacity-50 mt-1">
						{overview?.recentActivity.last24Hours || 0} orders today
					</p>
				</div>
			</div>

			<div className="space-y-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
					<div>
						<h2 className="text-lg font-semibold">Profit Analytics</h2>
						<p className="text-sm text-muted-foreground mt-1">
							{format(profitDateRange.from, "MMM d, yyyy")} —{" "}
							{format(profitDateRange.to, "MMM d, yyyy")}
						</p>
					</div>
					<Popover
						open={isProfitCalendarOpen}
						onOpenChange={handleProfitCalendarOpenChange}
					>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"w-full sm:w-[260px] justify-start text-left font-normal",
									!profitDateRange && "text-muted-foreground",
								)}
							>
								<CalendarIcon className="mr-2 h-4 w-4" />
								{profitDateRange.from && profitDateRange.to ? (
									<>
										{format(profitDateRange.from, "MMM d, yyyy")} -{" "}
										{format(profitDateRange.to, "MMM d, yyyy")}
									</>
								) : (
									<span>Pick a date range</span>
								)}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="end">
							<div className="flex flex-col">
								<Calendar
									mode="range"
									defaultMonth={profitDateRange.from}
									selected={profitCalendarSelection}
									onSelect={handleProfitDateRangeChange}
									numberOfMonths={2}
									disabled={(date) =>
										date > new Date() || date < new Date("2020-01-01")
									}
								/>
								<div className="flex items-center justify-end gap-2 p-3 border-t">
									<Button
										variant="outline"
										size="sm"
										onClick={handleCancelProfitDateRange}
									>
										Cancel
									</Button>
									<Button
										size="sm"
										onClick={handleApplyProfitDateRange}
										disabled={!hasProfitValidRange}
									>
										Apply
									</Button>
								</div>
							</div>
						</PopoverContent>
					</Popover>
				</div>

				{/* Loading State */}
				{isProfitLoading ? (
					<div className="space-y-6">
						<div className="grid gap-6 md:grid-cols-3">
							{[...Array(3)].map((_, i) => (
								<Skeleton key={i} className="h-32" />
							))}
						</div>
						<div className="grid gap-4 md:grid-cols-2">
							{[...Array(2)].map((_, i) => (
								<Skeleton key={i} className="h-64" />
							))}
						</div>
					</div>
				) : profitData && profitData.currencies.length > 0 ? (
					profitData.currencies.map((currencyData, idx) => (
						<div key={currencyData.currency} className="space-y-6">
							<div className="grid gap-4 grid-cols-1 md:grid-cols-3">
								<div className="stagger-in md:col-span-2 rounded-xl border p-6 bg-green-50/80 text-green-900 border-green-200/50 dark:bg-green-950/25 dark:text-green-300 dark:border-green-500/20">
									<div className="flex items-start justify-between">
										<div>
											<p className="text-sm font-medium opacity-70">
												{currencyData.currency} Profit
											</p>
											<p className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight tabular-nums mt-3">
												{formatCurrency(
													currencyData.totalProfit,
													currencyData.currency,
												)}
											</p>
											<div className="flex items-center gap-3 mt-4">
												{currencyData.profitChangePct !== 0 && (
													<span
														className={cn(
															"inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
															currencyData.profitChangePct > 0
																? "bg-[var(--trend-positive)]/15 text-[var(--trend-positive)]"
																: "bg-[var(--trend-negative)]/15 text-[var(--trend-negative)]",
														)}
													>
														{currencyData.profitChangePct > 0 ? (
															<ArrowUpRight className="size-3" />
														) : (
															<ArrowDownRight className="size-3" />
														)}
														{Math.abs(currencyData.profitChangePct).toFixed(1)}%
													</span>
												)}
												<span className="text-sm opacity-60">
													{(currencyData.profitMargin * 100).toFixed(1)}% margin
												</span>
											</div>
										</div>
										<div className="text-right">
											<p className="text-xs font-medium opacity-60">Volume</p>
											<p className="text-xl font-semibold tracking-tight tabular-nums mt-1">
												{formatCurrency(
													currencyData.totalVolume,
													currencyData.currency,
												)}
											</p>
											{currencyData.volumeChangePct !== 0 && (
												<span
													className={cn(
														"inline-flex items-center gap-0.5 text-xs font-medium mt-1",
														currencyData.volumeChangePct > 0
															? "text-[var(--trend-positive)]"
															: "text-[var(--trend-negative)]",
													)}
												>
													{currencyData.volumeChangePct > 0 ? (
														<ArrowUpRight className="size-3" />
													) : (
														<ArrowDownRight className="size-3" />
													)}
													{Math.abs(currencyData.volumeChangePct).toFixed(1)}%
												</span>
											)}
										</div>
									</div>
								</div>

								<Card>
									<CardContent className="pt-6">
										<p className="text-sm font-medium text-muted-foreground">
											Margin
										</p>
										<p className="text-3xl font-bold tracking-tight tabular-nums mt-2">
											{(currencyData.profitMargin * 100).toFixed(2)}%
										</p>
										{currencyData.marginChangePct !== 0 && (
											<span
												className={cn(
													"inline-flex items-center gap-0.5 text-xs font-medium mt-2",
													currencyData.marginChangePct > 0
														? "text-[var(--trend-positive)]"
														: "text-[var(--trend-negative)]",
												)}
											>
												{currencyData.marginChangePct > 0 ? (
													<ArrowUpRight className="size-3" />
												) : (
													<ArrowDownRight className="size-3" />
												)}
												{Math.abs(currencyData.marginChangePct).toFixed(1)}%
											</span>
										)}
									</CardContent>
								</Card>
							</div>
							<div className="grid gap-6 md:grid-cols-2">
								<Card>
									<CardHeader className="pb-2">
										<CardTitle className="text-base font-medium">
											{currencyData.currency} Volume
										</CardTitle>
										<CardDescription className="text-xs">
											Daily transaction volume
										</CardDescription>
									</CardHeader>
									<CardContent>
										<ResponsiveContainer width="100%" height={200}>
											<AreaChart data={currencyData.series}>
												<defs>
													<linearGradient
														id={`volumeGradient-${idx}`}
														x1="0"
														y1="0"
														x2="0"
														y2="1"
													>
														<stop
															offset="0%"
															stopColor="var(--chart-1)"
															stopOpacity={0.5}
														/>
														<stop
															offset="50%"
															stopColor="var(--chart-1)"
															stopOpacity={0.25}
														/>
														<stop
															offset="100%"
															stopColor="var(--chart-1)"
															stopOpacity={0}
														/>
													</linearGradient>
												</defs>
												<XAxis
													dataKey="date"
													tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
													tickLine={false}
													axisLine={false}
													tickFormatter={(value) => {
														const date = new Date(value);
														return `${date.getMonth() + 1}/${date.getDate()}`;
													}}
												/>
												<YAxis
													tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
													tickLine={false}
													axisLine={false}
													tickFormatter={(value) =>
														value >= 1000
															? `$${(value / 1000).toFixed(0)}k`
															: `$${value.toFixed(0)}`
													}
												/>
												<Tooltip
													content={
														<CustomTooltip
															formatter={(value: number) =>
																`$${value.toFixed(2)}`
															}
														/>
													}
												/>
												<Area
													type="monotone"
													dataKey="volume"
													stroke="var(--chart-1)"
													strokeWidth={2.5}
													fill={`url(#volumeGradient-${idx})`}
													dot={false}
												/>
											</AreaChart>
										</ResponsiveContainer>
									</CardContent>
								</Card>
								<Card>
									<CardHeader className="pb-2">
										<CardTitle className="text-base font-medium">
											{currencyData.currency} Profit
										</CardTitle>
										<CardDescription className="text-xs">
											Daily profit from service fees
										</CardDescription>
									</CardHeader>
									<CardContent>
										<ResponsiveContainer width="100%" height={200}>
											<AreaChart data={currencyData.series}>
												<defs>
													<linearGradient
														id={`profitGradient-${idx}`}
														x1="0"
														y1="0"
														x2="0"
														y2="1"
													>
														<stop
															offset="0%"
															stopColor="var(--chart-2)"
															stopOpacity={0.5}
														/>
														<stop
															offset="50%"
															stopColor="var(--chart-2)"
															stopOpacity={0.25}
														/>
														<stop
															offset="100%"
															stopColor="var(--chart-2)"
															stopOpacity={0}
														/>
													</linearGradient>
												</defs>
												<XAxis
													dataKey="date"
													tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
													tickLine={false}
													axisLine={false}
													tickFormatter={(value) => {
														const date = new Date(value);
														return `${date.getMonth() + 1}/${date.getDate()}`;
													}}
												/>
												<YAxis
													tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
													tickLine={false}
													axisLine={false}
													tickFormatter={(value) =>
														value >= 1000
															? `$${(value / 1000).toFixed(0)}k`
															: `$${value.toFixed(0)}`
													}
												/>
												<Tooltip
													content={
														<CustomTooltip
															formatter={(value: number) =>
																`$${value.toFixed(2)}`
															}
														/>
													}
												/>
												<Area
													type="monotone"
													dataKey="profit"
													stroke="var(--chart-2)"
													strokeWidth={2.5}
													fill={`url(#profitGradient-${idx})`}
													dot={false}
												/>
											</AreaChart>
										</ResponsiveContainer>
									</CardContent>
								</Card>
							</div>
							{idx < profitData.currencies.length - 1 && (
								<div className="border-t my-8" />
							)}
						</div>
					))
				) : (
					<Card className="border-dashed">
						<CardContent className="flex flex-col items-center justify-center py-12">
							<div className="text-center space-y-2">
								<p className="text-sm font-medium text-muted-foreground">
									No profit data available
								</p>
								<p className="text-xs text-muted-foreground">
									Try selecting a different date range
								</p>
							</div>
						</CardContent>
					</Card>
				)}
			</div>

			{/* Order Status Analytics */}
			<div className="space-y-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
					<div>
						<h2 className="text-lg font-semibold">Order Status Over Time</h2>
						<p className="text-sm text-muted-foreground mt-1">
							{format(orderStatusDateRange.from, "MMM d, yyyy")} —{" "}
							{format(orderStatusDateRange.to, "MMM d, yyyy")}
						</p>
					</div>
					<Popover
						open={isOrderStatusCalendarOpen}
						onOpenChange={handleOrderStatusCalendarOpenChange}
					>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"w-full sm:w-[260px] justify-start text-left font-normal",
									!orderStatusDateRange && "text-muted-foreground",
								)}
							>
								<CalendarIcon className="mr-2 h-4 w-4" />
								{orderStatusDateRange.from && orderStatusDateRange.to ? (
									<>
										{format(orderStatusDateRange.from, "MMM d, yyyy")} -{" "}
										{format(orderStatusDateRange.to, "MMM d, yyyy")}
									</>
								) : (
									<span>Pick a date range</span>
								)}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="end">
							<div className="flex flex-col">
								<Calendar
									mode="range"
									defaultMonth={orderStatusDateRange.from}
									selected={orderStatusCalendarSelection}
									onSelect={handleOrderStatusDateRangeChange}
									numberOfMonths={2}
									disabled={(date) =>
										date > new Date() || date < new Date("2020-01-01")
									}
								/>
								<div className="flex items-center justify-end gap-2 p-3 border-t">
									<Button
										variant="outline"
										size="sm"
										onClick={handleCancelOrderStatusDateRange}
									>
										Cancel
									</Button>
									<Button
										size="sm"
										onClick={handleApplyOrderStatusDateRange}
										disabled={!hasOrderStatusValidRange}
									>
										Apply
									</Button>
								</div>
							</div>
						</PopoverContent>
					</Popover>
				</div>

				{isOrderStatusLoading ? (
					<Skeleton className="h-96" />
				) : orderStatusData && orderStatusData.series.length > 0 ? (
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">
								Order Status
							</CardTitle>
							<CardDescription className="text-xs">
								Daily order counts by status
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ResponsiveContainer width="100%" height={320}>
								<AreaChart data={orderStatusData.series}>
									<defs>
										<linearGradient
											id="paidGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor="var(--chart-2)"
												stopOpacity={0.5}
											/>
											<stop
												offset="50%"
												stopColor="var(--chart-2)"
												stopOpacity={0.25}
											/>
											<stop
												offset="100%"
												stopColor="var(--chart-2)"
												stopOpacity={0}
											/>
										</linearGradient>
										<linearGradient
											id="pendingGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor="var(--chart-4)"
												stopOpacity={0.5}
											/>
											<stop
												offset="50%"
												stopColor="var(--chart-4)"
												stopOpacity={0.25}
											/>
											<stop
												offset="100%"
												stopColor="var(--chart-4)"
												stopOpacity={0}
											/>
										</linearGradient>
										<linearGradient
											id="expiredGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor="var(--muted-foreground)"
												stopOpacity={0.5}
											/>
											<stop
												offset="50%"
												stopColor="var(--muted-foreground)"
												stopOpacity={0.25}
											/>
											<stop
												offset="100%"
												stopColor="var(--muted-foreground)"
												stopOpacity={0}
											/>
										</linearGradient>
										<linearGradient
											id="cancelledGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor="var(--destructive)"
												stopOpacity={0.5}
											/>
											<stop
												offset="50%"
												stopColor="var(--destructive)"
												stopOpacity={0.25}
											/>
											<stop
												offset="100%"
												stopColor="var(--destructive)"
												stopOpacity={0}
											/>
										</linearGradient>
									</defs>
									<XAxis
										dataKey="date"
										tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
										tickLine={false}
										axisLine={false}
										tickFormatter={(value) => {
											const date = new Date(value);
											return `${date.getMonth() + 1}/${date.getDate()}`;
										}}
									/>
									<YAxis
										tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
										tickLine={false}
										axisLine={false}
									/>
									<Tooltip
										content={
											<CustomTooltip
												formatter={(value: number, name: string) => {
													const statusNames: Record<string, string> = {
														paid: "Paid",
														pending: "Pending",
														expired: "Expired",
														cancelled: "Cancelled",
													};
													return [value, statusNames[name] || name];
												}}
											/>
										}
									/>
									<Legend
										wrapperStyle={{
											paddingTop: "20px",
										}}
										iconType="circle"
										formatter={(value: string) => {
											const statusNames: Record<string, string> = {
												paid: "Paid",
												pending: "Pending",
												expired: "Expired",
												cancelled: "Cancelled",
											};
											return (
												<span className="text-xs font-medium text-muted-foreground">
													{statusNames[value] || value}
												</span>
											);
										}}
									/>
									<Area
										type="monotone"
										dataKey="paid"
										stroke="var(--chart-2)"
										strokeWidth={2.5}
										fill="url(#paidGradient)"
										dot={false}
									/>
									<Area
										type="monotone"
										dataKey="pending"
										stroke="var(--chart-4)"
										strokeWidth={2.5}
										fill="url(#pendingGradient)"
										dot={false}
									/>
									<Area
										type="monotone"
										dataKey="expired"
										stroke="var(--muted-foreground)"
										strokeWidth={2.5}
										fill="url(#expiredGradient)"
										dot={false}
									/>
									<Area
										type="monotone"
										dataKey="cancelled"
										stroke="var(--destructive)"
										strokeWidth={2.5}
										fill="url(#cancelledGradient)"
										dot={false}
									/>
								</AreaChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>
				) : (
					<Card className="border-dashed">
						<CardContent className="flex flex-col items-center justify-center py-12">
							<div className="text-center space-y-2">
								<p className="text-sm font-medium text-muted-foreground">
									No order data available
								</p>
								<p className="text-xs text-muted-foreground">
									Try selecting a different date range
								</p>
							</div>
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	);
};

export default DashboardPage;
